import Icon from "@afa-shared/afa-components/dist/Icon";
import { ContentLink } from "@components/Link/ContentLink";
import clsx from "clsx";
import { ReactNode } from "react";

import paginationStyles from "./Pagination.module.css";

/**
 * Builds a clean href by combining a base path, existing query parameters,
 * and a desired page number.
 *
 * - Keeps all existing query parameters except those with empty values.
 * - Updates or removes the "page" parameter:
 *   - page <= 1 → remove
 *   - page > 1 → set to that value
 * - Preserves any other query parameters in their current state.
 */
function buildHref(basePath: string, queryString: string | undefined, page: number): string {
  // Strip any existing "?" from the base path
  const cleanBase = basePath.split("?")[0] || "";

  // Initialize with existing params (if any)
  const params = new URLSearchParams(queryString?.replace(/^\?/, "") || "");

  // Remove empty params (e.g., foo=, bar=undefined)
  for (const [key, value] of Array.from(params.entries())) {
    if (value === "" || value === "undefined" || value === "null") {
      params.delete(key);
    }
  }

  params.set("page", String(page));

  // Construct final query string
  const qs = params.toString();
  return qs ? `${cleanBase}?${qs}` : cleanBase;
}

/**
 * Mark a page item with semantic classes so CSS can decide responsiveness.
 * - "core": always show (current, first, last, neighbors)
 * - "secondary-sm": hidden on very small screens
 * - "secondary-md": hidden on small/medium, shown from tablet/desktop
 */
function classifyPageItem(
  page: number | "DOTS",
  current: number,
  total: number,
): "core" | "secondary-sm" | "secondary-md" | "dots" {
  if (page === "DOTS") return "dots";

  // Core pages: first, last, current, and its immediate neighbors
  const isFirst = page === 1;
  const isLast = page === total;
  const isCurrent = page === current;
  const isNeighbor = Math.abs(page - current) === 1;

  if (isFirst || isLast || isCurrent || isNeighbor) return "core";

  // Then gradate visibility for the rest:
  // pages two steps away = show from medium+
  if (Math.abs(page - current) === 2) return "secondary-sm";

  // farther pages = show from larger screens
  return "secondary-md";
}

const getDottedPagination = (
  totalPaginationPages: number,
  currentPaginationPage: number,
  url: string,
  queryString: string,
  onItemClick: () => void,
): ReactNode[] => {
  // FIXED siblingCount for SSR determinism (no window access)
  const paginationRange = getPaginationRange(totalPaginationPages, currentPaginationPage, 1);

  const items: ReactNode[] = [];

  // Prev
  const hasPrev = currentPaginationPage > 1;
  const prevHref = buildHref(url, queryString, Math.max(1, currentPaginationPage - 1));
  hasPrev &&
    items.push(
      <ContentLink
        key="prev"
        href={prevHref}
        aria-label={`Gå till sida ${currentPaginationPage - 1}`}
        onClick={onItemClick}
        className={paginationStyles.navPrev}
        contentText={`Gå till sida ${currentPaginationPage - 1}`}
      >
        <Icon name="chevron_left" size="sm" className={paginationStyles.chevron} />
      </ContentLink>,
    );

  // Pages
  paginationRange.forEach((pageNumber, idx) => {
    if (pageNumber === "DOTS") {
      items.push(
        <span
          key={`dots-${idx}`}
          className={clsx(paginationStyles.navHelper, paginationStyles.dots)}
        >
          &#8230;
        </span>,
      );
      return;
    }

    const href = buildHref(url, queryString, pageNumber);
    const role = classifyPageItem(pageNumber, currentPaginationPage, totalPaginationPages);

    items.push(
      <ContentLink
        key={`page-${pageNumber}`}
        href={href}
        className={clsx({
          active: pageNumber === currentPaginationPage,
          [paginationStyles.core]: role === "core",
          [paginationStyles.secondarySm]: role === "secondary-sm",
          [paginationStyles.secondaryMd]: role === "secondary-md",
        })}
        aria-current={pageNumber === currentPaginationPage ? "page" : undefined}
        aria-label={
          pageNumber === currentPaginationPage
            ? `Sida ${pageNumber}, nuvarande sida`
            : `Gå till sida ${pageNumber}`
        }
        onClick={onItemClick}
        contentText={`Gå till sida ${pageNumber}`}
      >
        {pageNumber}
      </ContentLink>,
    );
  });

  // Next
  const hasNext = currentPaginationPage < totalPaginationPages;
  const nextHref = buildHref(
    url,
    queryString,
    Math.min(totalPaginationPages, currentPaginationPage + 1),
  );
  items.push(
    hasNext ? (
      <ContentLink
        key="next"
        href={nextHref}
        aria-label={`Gå till nästa sida, sida ${currentPaginationPage + 1}`}
        onClick={onItemClick}
        className={paginationStyles.navNext}
        contentText={`Gå till nästa sida, sida ${currentPaginationPage + 1}`}
      >
        <Icon name="chevron_right" size="sm" className={paginationStyles.chevron} />
      </ContentLink>
    ) : (
      <span key="next" className={paginationStyles.navHelper} />
    ),
  );

  return items;
};

/**
 * Returns an array like: [1, "DOTS", 4, 5, 6, "DOTS", total]
 * siblingCount controls how many numbers to show on each side of current.
 * This function is PURE and thus SSR/CSR stable.
 */
const getPaginationRange = (
  totalPageCount: number,
  currentPage: number,
  siblingCount = 0,
): Array<number | "DOTS"> => {
  const totalPageNumbers = siblingCount + 5;

  if (totalPageNumbers >= totalPageCount) {
    return [...Array(totalPageCount)].map((_, i) => 1 + i);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

  const shouldShowLeftDots = leftSiblingIndex >= 2;
  const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

  const firstPageIndex = [1];
  const lastPageIndex = [totalPageCount];

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 2 + 2 * siblingCount;
    const leftRange = [...Array(leftItemCount + 1)].map((_, i) => 1 + i);
    return [...leftRange, "DOTS", ...lastPageIndex];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = [...Array(rightItemCount)].map(
      (_, i) => totalPageCount - rightItemCount + 1 + i,
    );
    return [...firstPageIndex, "DOTS", ...rightRange];
  }

  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = [...Array(rightSiblingIndex - leftSiblingIndex + 1)].map(
      (_, i) => leftSiblingIndex + i,
    );
    return [...firstPageIndex, "DOTS", ...middleRange, "DOTS", ...lastPageIndex];
  }

  return [];
};

export default getDottedPagination;
